import { Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';

import { BrandingConfigService } from './branding-config.service';
import { BrandingConfig, BrandingService } from './branding.service';
import { SessionService } from './session.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class AppInitService {
  private brandingConfig: BrandingConfig | null = null;
  private readonly EXCLUDED_DOMAINS = ['app.talkgenie.ai',  'staging-frontend.talkgenie.ai', 'localhost', '127.0.0.1'];

  constructor(
    private brandingService: BrandingService,
    private brandingConfigService: BrandingConfigService,
    private sessionService: SessionService,
    private userSvc: UserService
  ) {}

  /**
   * Initialize the application by fetching branding and theming information
   */
  init(): Observable<any> {
    return this.initBrandingConfig();
   
  }

  /**
   * Determine if branding should be fetched by domain or user ID
   */
  private shouldFetchByDomain(): boolean {
    const currentDomain = window.location.hostname;
    return !this.EXCLUDED_DOMAINS.includes(currentDomain);
  }

  /**
   * Get the appropriate branding configuration based on domain and user state
   */
  private fetchBrandingConfig(): Observable<BrandingConfig | null> {
    if (this.shouldFetchByDomain()) {
      // Fetch by domain for custom domains
      return this.brandingService.getBrandingConfigByDomain();
    } else {
      // For app.talkgenie.ai and localhost, check if user is logged in
      const currentUser = this.sessionService.getUserValue();
      if (currentUser && currentUser?.user?.id && this.sessionService.getUserWorkspace()?.uid) {
        // User is logged in, fetch by user ID
        return this.brandingService.getBrandingConfigByWorkspace(currentUser.user.id.toString());
      } else {
        // User not logged in, return null to use defaults
        return of(null);
      }
    }
  }

  private initBrandingConfig(): Observable<boolean> {
    return this.fetchBrandingConfig().pipe(
        tap((config: BrandingConfig | null) => {
          this.processAndApplyBrandingConfig(config);
        }),
        catchError((error) => {
          console.warn('Failed to load branding configuration, using defaults:', error);
          this.processAndApplyBrandingConfig(null);
          return of(null);
        }),
        map(() => true)
      );
  }

  /**
   * Process and apply branding configuration (decode, parse, and apply)
   */
  public processAndApplyBrandingConfig(config: BrandingConfig | null): void {
    if (config) {
      this.brandingConfig = {
        ...config,
        brandingColorPalette: JSON.parse(decodeURIComponent(config.brandingColorPalette as any)),
      };
    } else {
      this.brandingConfig = null;
    }
    
    this.brandingConfigService.setLoaded(true);
    this.brandingConfigService.applyBrandingConfig(this.brandingConfig);
  }

  /**
   * Get branding configuration when user logs in
   */
  getBrandingConfigOnLogin(userId: string): void {
    if (this.shouldFetchByDomain()) {
      // For custom domains, continue using domain-based branding
      this.brandingService.getBrandingConfigByDomain().pipe(
        tap((config) => {
          this.processAndApplyBrandingConfig(config);
        }),
        catchError((error) => {
          console.warn('Failed to refresh branding on login:', error);
          return of(null);
        })
      ).subscribe();
    } else {
      // For app.talkgenie.ai and localhost, fetch by user ID
      this.brandingService.getBrandingConfigByWorkspace(userId).pipe(
        tap((config) => {
          this.processAndApplyBrandingConfig(config);
        }),
        catchError((error) => {
          console.warn('Failed to refresh branding on login:', error);
          return of(null);
        })
      ).subscribe();
    }
  }


  /**
   * Get the current branding configuration
   */
  getBrandingConfig(): BrandingConfig | null {
    return this.brandingConfig;
  }

  /**
   * Get current domain for debugging/logging
   */
  getCurrentDomain(): string {
    return window.location.hostname;
  }

  /**
   * Check if current domain is excluded from domain-based branding
   */
  isExcludedDomain(): boolean {
    return !this.shouldFetchByDomain();
  }
}
